import React, { useState } from "react";
import { css } from "glamor";
import toast from "react-hot-toast";
// @ts-ignore
import { colors } from "../../helpers/styles";
// @ts-ignore
import { handleAPIError } from "../../helpers/handleErrors";
import Input from "../toolBox/Input";
import Button from "../toolBox/Button";

export default function Admin() {
  const [impersonatedToken, setImpersonatedToken] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [lastImpersonatedUser, setLastImpersonatedUser] = useState("");

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const impersonateUser = async (email: string, password: string) => {
    console.log("impersonateUser", email);
    // Clear previous errors
    setError("");

    // Validate email
    if (!email.trim()) {
      setError("Email address is required");
      return;
    }

    if (!validateEmail(email.trim())) {
      setError("Please enter a valid email address");
      return;
    }

    // Validate password
    if (!password.trim()) {
      setError("Password is required");
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem("graphcoolToken");
      if (!token) {
        throw new Error("No authentication token found");
      }
      let domain = (import.meta as any).env.VITE_APP_GRAPHCOOL_URI;
      if (domain[domain.length - 1] === "/") {
        domain = domain.slice(0, -1);
      }
      let backendUrl =
        domain + `/utils/sys-user-sw?email=${encodeURIComponent(email.trim())}`;

      const response = await fetch(backendUrl, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          password: password.trim(),
        }),
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error(
            "Unauthorized - Invalid credentials or expired token"
          );
        } else if (response.status === 404) {
          throw new Error("User not found");
        } else if (response.status === 403) {
          throw new Error("Insufficient permissions to impersonate users");
        } else {
          throw new Error(`Request failed with status ${response.status}`);
        }
      }

      const data = await response.json();
      console.log("Impersonation result:", data);
      setImpersonatedToken(data.token);
      setLastImpersonatedUser(email.trim());
      setEmail("");
      setPassword("");

      toast.success(`Successfully impersonated user: ${email.trim()}`);
    } catch (err) {
      console.error("Impersonation error:", err);

      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError("An unexpected error occurred");
      }

      handleAPIError(err, { action: "impersonate_user" });
      toast.error("Failed to impersonate user");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    impersonateUser(email, password);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    if (error) {
      setError("");
    }
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
    if (error) {
      setError("");
    }
  };

  const handleBecomeUser = () => {
    // set token to local storage
    localStorage.setItem("graphcoolToken", impersonatedToken);
    // redirect to /
    window.location.href = "/";
  };

  const handleClear = () => {
    setEmail("");
    setPassword("");
    setError("");
  };

  const isFormValid = email.trim() && password.trim() && !loading;

  return (
    <div
      {...css({
        maxWidth: 600,
        margin: "0 auto",
        padding: "32px 16px",
        fontFamily: "Museo Sans, Futura, Trebuchet MS, Arial, sans-serif",
        minHeight: "100vh",
      })}
    >
      {/* Header */}
      <div
        {...css({
          textAlign: "center",
          marginBottom: 48,
        })}
      >
        <h1
          {...css({
            fontSize: 32,
            fontWeight: 500,
            color: colors.es,
            margin: 0,
            marginBottom: 8,
          })}
        >
          Admin Panel
        </h1>
        <p
          {...css({
            fontSize: 16,
            fontWeight: 300,
            color: colors.ff,
            margin: 0,
            lineHeight: "24px",
          })}
        >
          Impersonate users for testing and support purposes
        </p>
      </div>

      {/* Success Card - Token Available */}
      {impersonatedToken && (
        <div
          {...css({
            background: "white",
            borderRadius: 12,
            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.08)",
            border: `2px solid ${colors.green}`,
            padding: 24,
            marginBottom: 24,
          })}
        >
          <div
            {...css({
              display: "flex",
              alignItems: "center",
              gap: 12,
              marginBottom: 16,
            })}
          >
            <span
              className="bi_interface-check-circle"
              {...css({ color: colors.green, fontSize: 24 })}
            />
            <div>
              <h3
                {...css({
                  fontSize: 18,
                  fontWeight: 500,
                  color: colors.dark_green,
                  margin: 0,
                  marginBottom: 4,
                })}
              >
                Impersonation Successful
              </h3>
              <p
                {...css({
                  fontSize: 14,
                  fontWeight: 300,
                  color: colors.dark_green,
                  margin: 0,
                })}
              >
                Ready to access {lastImpersonatedUser}'s account
              </p>
            </div>
          </div>
          <div
            {...css({
              display: "flex",
              justifyContent: "flex-end",
              paddingTop: 16,
              borderTop: `1px solid ${colors.light_green}`,
            })}
          >
            <Button
              name="Continue as User"
              theme="green"
              size="medium"
              action={handleBecomeUser}
            />
          </div>
        </div>
      )}

      {/* Main Card */}
      <div
        {...css({
          background: "white",
          borderRadius: 12,
          boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.08)",
          border: "1px solid #E2E2E2",
          padding: 32,
          marginBottom: 24,
        })}
      >
        <div
          {...css({
            marginBottom: 32,
          })}
        >
          <h2
            {...css({
              fontSize: 20,
              fontWeight: 500,
              color: colors.es,
              margin: 0,
              marginBottom: 8,
              display: "flex",
              alignItems: "center",
              gap: 8,
            })}
          >
            <span
              className="bi_interface-user-single"
              {...css({ color: colors.blue })}
            />
            User Impersonation
          </h2>
          <p
            {...css({
              fontSize: 14,
              fontWeight: 300,
              color: colors.ff,
              margin: 0,
              lineHeight: "20px",
            })}
          >
            Enter the user's credentials to access their account
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <div {...css({ marginBottom: 24 })}>
            <Input
              id="impersonate-email"
              label="User Email Address"
              name="email"
              type="email"
              value={email}
              handleChange={handleEmailChange}
              placeholder="Enter user's email address"
              disabled={loading}
              isInvalid={!!error}
              customStyle={{ marginBottom: 0 }}
            />
          </div>

          <div {...css({ marginBottom: 24 })}>
            <Input
              id="impersonate-password"
              label="Password"
              name="password"
              type="password"
              value={password}
              handleChange={handlePasswordChange}
              placeholder="Enter your own password"
              disabled={loading}
              isInvalid={!!error}
              customStyle={{ marginBottom: 0 }}
            />
          </div>

          {error && (
            <div
              {...css({
                background: colors.transparent_red,
                border: `1px solid ${colors.red}`,
                borderRadius: 8,
                padding: 12,
                marginBottom: 24,
              })}
            >
              <div
                {...css({
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                })}
              >
                <span
                  className="bi_interface-alert-circle"
                  {...css({ color: colors.red, fontSize: 16 })}
                />
                <span
                  {...css({
                    fontSize: 14,
                    fontWeight: 400,
                    color: colors.red,
                  })}
                >
                  {error}
                </span>
              </div>
            </div>
          )}

          <div
            {...css({
              display: "flex",
              justifyContent: "flex-end",
              gap: 12,
            })}
          >
            <Button
              name={loading ? "Authenticating..." : "Impersonate User"}
              theme="blue"
              size="medium"
              disabled={!isFormValid}
              loading={loading}
              type="submit"
            />
            <Button
              name="Clear"
              theme="grey-border"
              size="medium"
              action={handleClear}
              disabled={loading || (!email && !password)}
            />
          </div>
        </form>
      </div>

      {/* Last Impersonated User */}
      {lastImpersonatedUser && !impersonatedToken && (
        <div
          {...css({
            background: colors.light_green,
            border: `1px solid ${colors.green}`,
            borderRadius: 8,
            padding: 16,
            marginBottom: 24,
          })}
        >
          <div
            {...css({
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginBottom: 4,
            })}
          >
            <span
              className="bi_interface-check"
              {...css({ color: colors.green, fontSize: 16 })}
            />
            <span
              {...css({
                fontSize: 14,
                fontWeight: 500,
                color: colors.dark_green,
              })}
            >
              Last Impersonated User
            </span>
          </div>
          <p
            {...css({
              fontSize: 14,
              fontWeight: 300,
              color: colors.dark_green,
              margin: 0,
              marginLeft: 24,
            })}
          >
            {lastImpersonatedUser}
          </p>
        </div>
      )}

      {/* Warning Notice */}
      <div
        {...css({
          background: colors.transparent_orange,
          border: `1px solid ${colors.orange}`,
          borderRadius: 8,
          padding: 16,
        })}
      >
        <div
          {...css({
            display: "flex",
            alignItems: "flex-start",
            gap: 8,
          })}
        >
          <span
            className="bi_interface-alert-triangle"
            {...css({ color: colors.orange, fontSize: 16, marginTop: 2 })}
          />
          <div>
            <h3
              {...css({
                fontSize: 14,
                fontWeight: 500,
                color: colors.orange,
                margin: 0,
                marginBottom: 4,
              })}
            >
              Important Notice
            </h3>
            <p
              {...css({
                fontSize: 14,
                fontWeight: 300,
                color: colors.orange,
                margin: 0,
                lineHeight: "20px",
              })}
            >
              User impersonation should only be used for legitimate testing and
              support purposes. All impersonation activities are logged and
              monitored.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
